import React, {
  useState,
  ChangeEvent,
  useMemo,
  useEffect,
  useRef,
} from "react";
import Button from "src/components/core/Button";
import Dialog from "src/components/common/Dialog";
import { Input } from "src/components/core/Input";
import { useCreateSavedSearchMutation } from "../hooks/useCreateSavedSearchMutation";
import { SavedSearchSelector } from "../search/SavedSearchSelector";
import { ComboBoxListOption } from "src/components/core/ComboBox";
import { useNavigate } from "react-router-dom";
import { toast } from "src/components/shadcn/useToast";
import { SavedSearch, SearchRegion } from "src/types/neighborhoodSearch";
import { GeoRequest } from "src/libs/landvoice-api/client";
import { InfoIcon } from "lucide-react";
import { createDatePrettyName } from "src/utils/strings";
import { useFetchCreditsBalance } from "../hooks/useFetchCreditsBalance";
import { SearchErrorMessage } from "../search/SearchSidebar/SearchErrorMessage";
import { Label } from "src/components/shadcn";

export interface CloneListDialogProps {
  onClose: () => void;
  open: boolean;
  initialSelectedSearch?: SavedSearch;
}

const convertSearchRegionToGeoRequest = (
  searchRegion: SearchRegion
): GeoRequest | undefined => {
  if (!searchRegion) return undefined;

  switch (searchRegion.type) {
    case "circle":
      if (!searchRegion.circle) return undefined;
      return {
        circle: {
          center: {
            latitude: searchRegion.circle.circleCenter.lat,
            longitude: searchRegion.circle.circleCenter.lng,
          },
          radius: searchRegion.circle.circleRadius,
        },
      };
    case "polygon":
      if (!searchRegion.polygonVertices) return undefined;
      return {
        polygon: {
          points: searchRegion.polygonVertices.map((vertex) => ({
            latitude: vertex.lat,
            longitude: vertex.lng,
          })),
        },
      };
    case "rectangle":
      if (!searchRegion.rectangleBounds) return undefined;
      return {
        polygon: {
          points: [
            {
              latitude: searchRegion.rectangleBounds.north,
              longitude: searchRegion.rectangleBounds.west,
            },
            {
              latitude: searchRegion.rectangleBounds.north,
              longitude: searchRegion.rectangleBounds.east,
            },
            {
              latitude: searchRegion.rectangleBounds.south,
              longitude: searchRegion.rectangleBounds.east,
            },
            {
              latitude: searchRegion.rectangleBounds.south,
              longitude: searchRegion.rectangleBounds.west,
            },
            {
              latitude: searchRegion.rectangleBounds.north,
              longitude: searchRegion.rectangleBounds.west,
            },
          ],
        },
      };
    default:
      return undefined;
  }
};

export const CloneListDialog: React.FC<CloneListDialogProps> = ({
  onClose,
  open,
  initialSelectedSearch,
}) => {
  const [newSearchName, setNewSearchName] = useState("");
  const [selectedSearch, setSelectedSearch] = useState<SavedSearch | null>(
    initialSelectedSearch || null
  );
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [hasAutoSelected, setHasAutoSelected] = useState(false);

  const { mutateAsync: createSavedSearch, isPending: isCreatingSavedSearch } =
    useCreateSavedSearchMutation();
  const { data: creditsData } = useFetchCreditsBalance();
  const availableCredits = creditsData?.balance;
  const formattedCredits = creditsData?.formattedBalance;

  const [selectedSearchOption, setSelectedSearchOption] = useState<
    ComboBoxListOption | null | undefined
  >(
    initialSelectedSearch
      ? {
          label: initialSelectedSearch.name,
          value: initialSelectedSearch.id,
        }
      : null
  );

  const handleSelectedSearchChange = (
    newSelectedSearch: SavedSearch | null
  ) => {
    setSelectedSearch(newSelectedSearch);
  };

  const totalProperties = useMemo(() => {
    return selectedSearch?.result_count || 0;
  }, [selectedSearch]);

  const hasInsufficientCredits = useMemo(() => {
    return availableCredits !== null && availableCredits < totalProperties;
  }, [availableCredits, totalProperties]);

  const handleSearchSelect = (item: ComboBoxListOption | null | undefined) => {
    setSelectedSearchOption(item);
  };

  const firstSelectedSearchOption = selectedSearchOption;

  const singleListName = useMemo(
    () => firstSelectedSearchOption?.label,
    [firstSelectedSearchOption]
  );

  const firstSelectedSearch = selectedSearch;

  // Set default search name when a search is selected
  useEffect(() => {
    if (singleListName) {
      setNewSearchName(createDatePrettyName(singleListName));
      // Auto-select the text only once after a short delay to ensure the input is rendered
      if (!hasAutoSelected) {
        setTimeout(() => {
          inputRef.current?.select();
          setHasAutoSelected(true);
        }, 50);
      }
    }
  }, [singleListName, hasAutoSelected]);

  // Reset auto-select flag when dialog is closed or opened
  useEffect(() => {
    if (!open) {
      setHasAutoSelected(false);
    }
  }, [open]);

  const submitButtonLabel = useMemo(() => {
    return "Re-run Search";
  }, []);

  const handleSubmitClick = async () => {
    if (!firstSelectedSearch) return;

    const searchFilters = firstSelectedSearch.search_parameters.searchFilters;
    if (searchFilters) {
      const geoRequest = firstSelectedSearch.search_parameters.searchRegion
        ? convertSearchRegionToGeoRequest(
            firstSelectedSearch.search_parameters.searchRegion
          )
        : undefined;
      const { id: newSearchId } = await createSavedSearch({
        listName: newSearchName,
        listOptions: {
          geo: geoRequest,
          bedrooms: [searchFilters.bedrooms.min, searchFilters.bedrooms.max],
          bathrooms: [searchFilters.bathrooms.min, searchFilters.bathrooms.max],
          acreage: searchFilters.acreage
            ? [searchFilters.acreage.min, searchFilters.acreage.max]
            : undefined,
          squareFootage: [
            searchFilters.squareFootage.min,
            searchFilters.squareFootage.max,
          ],
          yearBuilt: [searchFilters.yearBuilt.min, searchFilters.yearBuilt.max],
          propertyType: searchFilters.propertyType.split(","),
          companyOwned: searchFilters.showOwnersOccupied,
        },
        searchRegion: firstSelectedSearch.search_parameters.searchRegion,
      });
      if (newSearchId) {
        navigate(`/ns/searches/map?selected=${newSearchId}`);
      }
      toast({
        title: `${newSearchName} created`,
        description: (
          <div
            dangerouslySetInnerHTML={{
              __html: `A new list of properties has been created using the same filters as <strong>${singleListName}</strong>`,
            }}
          />
        ),
        variant: "success",
      });
      onClose();
    }
  };

  return (
    <Dialog onOpenChange={onClose} isOpen={open} title="Re-run Search">
      <div className="flex flex-col gap-5 mt-5">
        {!selectedSearchOption && (
          <>
            <SavedSearchSelector
              selectedItem={selectedSearchOption}
              onSelect={handleSearchSelect}
              onSelectedSearchChange={handleSelectedSearchChange}
              disableNonRegionSearches
            />
            <div className="flex flex-row gap-2">
              <InfoIcon className="w-4 h-4" />
              <p>
                Searches cannot be re-run for combined lists nor single address
                searches
              </p>
            </div>
          </>
        )}
        {selectedSearchOption && (
          <>
            {hasInsufficientCredits ? (
              <div className="text-sm text-gray-500 text-center p-4">
                <SearchErrorMessage
                  error={{ name: "MonthlyLimitExceeded", message: "" }}
                />
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-2">
                  <p>
                    Search selected: <strong>{selectedSearchOption?.label}</strong>
                  </p>
                  <Label className="mt-2">Name this search (or use the default)</Label>
                  <Input
                    ref={inputRef}
                    autoFocus
                    value={newSearchName}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setNewSearchName(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newSearchName && !isCreatingSavedSearch && !hasInsufficientCredits) {
                        handleSubmitClick();
                      }
                    }}
                    placeholder={"Enter a name for the new list"}
                  />
                </div>
                <p>
                  Click <strong>{submitButtonLabel}</strong> to use up to{" "}
                  <strong>{totalProperties}</strong> credits to create a new
                  search using the same filters and region as{" "}
                  <strong>{selectedSearchOption?.label}</strong> with the latest
                  available contact information.
                  {availableCredits !== null && (
                    <div className="mt-2">
                      Available credits: <strong>{formattedCredits}</strong>
                    </div>
                  )}
                </p>
                <div className="flex flex-row gap-2 justify-end mt-8">
                  <Button color="secondary" onClick={onClose} variant="outline">
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    variant="default"
                    onClick={handleSubmitClick}
                    isLoading={isCreatingSavedSearch}
                    disabled={!firstSelectedSearch || isCreatingSavedSearch}
                  >
                    {submitButtonLabel}
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
};
